import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { TextField, CircularProgress, Paper, Popper } from "@mui/material";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Error from 'src/shared/components/customError/Error';
import {
  useStyles,
  Wrapper,
  Label,
  ErrorContainer,
} from './styles';

type CustomAutocompleteProps<TFieldValues extends FieldValues> = {
  name: string;
  error?: any;
  label?: string;
  placeholder: string;
  control: Control<TFieldValues>;
  options?: any[];
  optionKey?: string;
  isLoading: boolean;
  onChangeCallback: (inputValue: string) => void;
  onScrollCallback: (event: any) => void;
  onCloseCallback?: () => void;
  colorErrorMessage?: string;
  optionsDisabled?: any[];
};

const CustomAutocomplete = <TFieldValues extends FieldValues>({
  name,
  error = null,
  label,
  control,
  options = [],
  optionKey = 'name',
  placeholder,
  isLoading,
  onChangeCallback,
  onScrollCallback,
  onCloseCallback,
  colorErrorMessage = 'value_35',
  optionsDisabled = [],
}: CustomAutocompleteProps<TFieldValues>) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const renderPopper = useCallback(
    (inProps: any) => <Popper {...inProps} className={clsx(inProps.className, classes.root)} />,
    [classes.root]
  );

  const renderPaper = useCallback(
    (inProps: any) => <Paper {...inProps} className={clsx(inProps.className, classes.root)} />,
    [classes.root]
  );

  const disabledSet = new Set(
    optionsDisabled.map((d) => String(d?.id ?? d?.[optionKey] ?? d))
  );

  return (
    <Wrapper>
      {label && <Label>{label}</Label>}

      <Controller
        name={name as FieldPath<TFieldValues>}
        control={control}
        render={({ field: { onChange, ...field } }) => (
          <Autocomplete
            {...field}
            freeSolo
            className={clsx(classes.root, classes.regular_light_bg_var2)}
            options={options}
            loading={isLoading}
            isOptionEqualToValue={(opt: any, val: any) => {
              const optKey = String(opt?.id ?? opt?.[optionKey] ?? opt);
              const valKey = String(val?.id ?? val?.[optionKey] ?? val);
              return optKey === valKey;
            }}
            getOptionDisabled={(option) => {
              const key = String(option?.id ?? option?.[optionKey] ?? option);
              return disabledSet.has(key);
            }}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option?.[optionKey] ?? ''
            }
            onInputChange={(_, newInputValue) => {
              if (newInputValue === '') {
                onChange(null);
              } else {
                onChangeCallback(newInputValue);
                const option = options.find((o) => o?.[optionKey] === newInputValue);
                onChange(option || newInputValue);
              }
            }}
            onOpen={() => {
              setIsOpen(true);
              onChangeCallback('');
            }}
            onClose={() => {
              setIsOpen(false);
              onCloseCallback?.();
            }}
            ListboxProps={{ onScroll: onScrollCallback }}
            PaperComponent={renderPaper}
            PopperComponent={renderPopper}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                placeholder={placeholder}
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && <CircularProgress color="inherit" size={20} />}
                      <span
                        className={clsx(classes.arrowIcon, {
                          [classes.arrowIconOpen]: isOpen,
                        })}
                      />
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      />

      <ErrorContainer>
        <Error
          color={colorErrorMessage}
          name={name}
          error={error}
          label={label?.replace(/\*\s*/g, '') ?? ''}
        />
      </ErrorContainer>
    </Wrapper>
  );
};

export default CustomAutocomplete;
